import { IParaviewColumnMapping, ParaviewFilter, ParaviewFilterPlus } from "@/types/dataset";
import { LayerNameToColumnLayerIdentifier } from "@/utils/DisplayPane";
import { NonDrillholeLayerConfigType } from "@/utils/DisplayPaneConstants";
import { defineStore } from "pinia";
import { useActiveStore as active } from "./active";
import { useDisplayPaneStore as displayPane } from "./displayPane";
import { useLayerStore as layer } from "./layer";
import { usePvwStore as pvw } from "./pvw";
interface ActiveFiltersMetadata {
	[viewId: string]: {
		[datasetId: string]: {
			aggregatedFilters: ParaviewFilterPlus[];
		};
	};
}

export interface FiltersMenuState {
	[viewId: string]: {
		[datasetId: string]: {
			visible: boolean;
			open: boolean;
			selected: boolean;
		};
	};
}
export const useFiltersStore = defineStore("filters", {
	state: () =>
		({
			saveStateFilters: null,
			saveStateFiltersDisplayPane: null,
			filtersIconClickedCount: 0,
			filtersIconClickedLayerName: "",
			loadSavedStateFilters: false,
			cloneInProgressFilters: null
		}) as {
			saveStateFilters: ActiveFiltersMetadata | null;
			saveStateFiltersDisplayPane: FiltersMenuState | null;
			filtersIconClickedCount: number;
			filtersIconClickedLayerName: string;
			loadSavedStateFilters: boolean;
			// temporary field during the clone process so we can map the cloned
			// metadata to the new filter id sent by Paraview
			cloneInProgressFilters: ActiveFiltersMetadata | null;
		},
	getters: {
		// Triggered by a layer state update from pvw, among other things
		SSA_ACTIVE_FILTERS_MAP_BY_DATASET(): ActiveFiltersMetadata {
			const map = layer().SSA_LAYER_STATES;
			const viewId = active().SSA_ACTIVE_VIEW;
			const layerName = active().SSA_ACTIVE_LAYER;

			let cachedFilters = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET ?? {};
			const cloneInProgress: boolean = this.cloneInProgressFilters != null;

			// Saved state is initially applied all at once, and then individually for each filter when it's loaded; this lets status be displayed correctly in the UI, and must be done in combination with individual handling when each layer is loaded
			if (this.loadSavedStateFilters && this.saveStateFilters) {
				cachedFilters = this.saveStateFilters;
				this.loadSavedStateFilters = false;
			}

			// aggregate layer filters for listing in display pane
			if (!cachedFilters[viewId]) cachedFilters[viewId] = {};

			if (map[viewId]?.[layerName]?.filters) {
				const dataset = active().SSA_ACTIVE_DATASET;

				const datasetId = dataset?.id; // if no dataset is active, we are loading a saved state and can wait for a dataset to be selected
				if (!datasetId) return cachedFilters;
				if (!cachedFilters[viewId][datasetId])
					cachedFilters[viewId][datasetId] = {
						aggregatedFilters: []
					};
				if (!cachedFilters[viewId][datasetId].aggregatedFilters)
					cachedFilters[viewId][datasetId].aggregatedFilters = [];

				// make sure we are syncing up with remote state
				const newFilters = map[viewId][layerName].filters as ParaviewFilter[];

				let newFilterOrder: string | undefined;
				let filterToRemoveId: string | undefined;
				let cachedFiltersCurrent = cachedFilters[viewId][datasetId].aggregatedFilters;

				let orderIncrement = 0;
				const cachedFiltersMap = new Map(
					cachedFiltersCurrent.map((filter: ParaviewFilterPlus) => {
						if (filter.hideButKeepOrderPlaceholder) {
							filterToRemoveId = filter.id;
							newFilterOrder = filter.order;
						}
						orderIncrement = Math.max(parseInt(filter.order) + 1, orderIncrement + 1);
						return [filter.id, filter];
					})
				);

				const duplicateFiltersMap = {} as any;

				// update filters list based on paraview filter id
				newFilters.forEach((pvFilter: ParaviewFilter) => {
					const pvFilterProp = pvFilter.Scalars[1].split("__DATETIME__")[0];
					// Edge case for loading save state: Paraview sometimes hallucinates a duplicate filter. We should remove it.
					const duplicateFiltersIdentifier = layerName + "_" + pvFilterProp;
					if (duplicateFiltersMap[duplicateFiltersIdentifier]) {
						pvw().PVW_FILTER_THRESHOLD_REMOVE({
							layerName,
							viewId,
							filterId: pvFilter.id
						});
					} else {
						duplicateFiltersMap[duplicateFiltersIdentifier] = true;
					}
					// end edge case handling

					// add extra metadata, and updates from paraview to our cached data
					const layerDetails = dataset.paraviewLayersInfoSplit[layerName];
					if (!layerDetails) return; // if the active layer swaps, we might get filters from a previous active layer; we can just ignore these filters.
					const displayPaneLabel = layerDetails.displayPaneLabel;
					const layerNameSplit = layerName.split("/");
					let layerIdentifier = layerNameSplit.pop();
					if (layerDetails.layerConfigType != NonDrillholeLayerConfigType)
						layerIdentifier = LayerNameToColumnLayerIdentifier(
							layerNameSplit,
							layerIdentifier
						);
					const propDetails = dataset.datasetColumnMapping.find(
						(column: IParaviewColumnMapping) =>
							column.column == pvFilterProp &&
							column.layerIdentifier == layerIdentifier
					);
					const cachedFilter: ParaviewFilterPlus | undefined = cachedFiltersMap.get(
						pvFilter.id
					);

					// caching absMin/absMax is only required to avoid updating the active layer on select / unselect (where you may be selecting or unselecting filters from multiple layers)
					let absMin = 1;
					let absMax = -1;
					if (active().SSA_ACTIVE_LAYER_STATE.fields?.[pvFilter.Scalars[1]]) {
						absMin = active().SSA_ACTIVE_LAYER_STATE.fields[pvFilter.Scalars[1]].min;
						absMax = active().SSA_ACTIVE_LAYER_STATE.fields[pvFilter.Scalars[1]].max;
					} else if (cachedFilter) {
						if (cachedFilter.absMin) absMin = cachedFilter.absMin;
						if (cachedFilter.absMax) absMax = cachedFilter.absMax;
					}
					let usePreviousFiltersOrder: string | undefined;

					// handle order management for property swap
					if (
						newFilterOrder != undefined &&
						filterToRemoveId != undefined &&
						!cachedFilter
					) {
						usePreviousFiltersOrder = newFilterOrder;
						cachedFiltersMap.delete(filterToRemoveId);
					}
					// end order management

					// load from a saved state
					let saveStateFilterDetails = pvFilter as Partial<ParaviewFilterPlus>;

					// Update based on saved state (necessary to do individually for inactive/unselected layers)
					if (this.saveStateFilters?.[viewId]?.[datasetId]) {
						// remove the cached value to prevent future uses, and use properties from the saved state even if the filter id is different
						this.saveStateFilters[viewId][datasetId].aggregatedFilters =
							this.saveStateFilters[viewId][datasetId].aggregatedFilters.filter(
								(filter) => {
									if (layerName != filter.layerName) return true; // if the layer is different, save the saveStateFilter filter for when the layer matches

									const filterProp = filter.Scalars[1].split("__DATETIME__")[0];
									if (filterProp == pvFilterProp) {
										saveStateFilterDetails = {
											...saveStateFilterDetails,
											...filter,
											layerUnselectedOnSaveState: false, // if pvFilter exists, we've already got a created filter on the paraview side and don't need to recreate it
											hideMetadataOnlyFilter: false
										};
										return false; // if these are true, we're adding the meta data this loop
									} else {
										return true;
									}
								}
							);
					} else if (
						cloneInProgress &&
						this.cloneInProgressFilters?.[viewId]?.[datasetId]?.aggregatedFilters &&
						this.cloneInProgressFilters[viewId][datasetId].aggregatedFilters.length > 0
					) {
						// For each cloned filter, if it matches the newly created filter from Paraview
						// by view id, dataset id, and prop name, merge the cloned metadata into the final object.
						// Don't mutate filtersArray - it's a reference to the original copy
						const filtersArray =
							this.cloneInProgressFilters[viewId][datasetId].aggregatedFilters;
						for (const filter of filtersArray) {
							if (layerName != filter.layerName) continue; // skip - not this one

							const filterProp = filter.Scalars[1].split("__DATETIME__")[0];
							if (filterProp == pvFilterProp) {
								saveStateFilterDetails = {
									...saveStateFilterDetails,
									...filter,
									layerUnselectedOnSaveState: false,
									hideMetadataOnlyFilter: false
								};
								break; // only one will match
							}
						}
					}

					// end save state loading
					if (!usePreviousFiltersOrder) orderIncrement++;
					cachedFiltersMap.set(pvFilter.id, {
						order: usePreviousFiltersOrder ?? String(orderIncrement),
						...saveStateFilterDetails,
						...cachedFilter, // persist properties set in the UI, like hide
						...pvFilter,
						absMin,
						absMax,
						layerName,
						displayPaneLabel,
						propLabel: propDetails?.label,
						itemAddedPersistMetadata: false,
						hideMetadataOnlyFilter: false
					});
				});

				// set temporary cloned map back to null so we don't do the clone again
				this.cloneInProgressFilters = null;

				cachedFiltersCurrent = Array.from(cachedFiltersMap.values()); // cached filters, combined with the new filters sent by paraview
				let filterSwappedOnSaveState: ParaviewFilterPlus | undefined;
				let replacingFilterId: string | undefined;

				// remove filters for this layerName if they're not on paraview (newFilters)
				cachedFiltersCurrent = cachedFiltersCurrent.filter((item: ParaviewFilterPlus) => {
					if (item.layerName !== layerName) return true;
					if (item.hideButKeepOrderPlaceholder) return true;
					if (item.itemAddedPersistMetadata) {
						const replacingFilter = newFilters.find(
							(newItem: ParaviewFilter) =>
								newItem.Scalars[1].split("__DATETIME__")[0] ===
								item.Scalars[1].split("__DATETIME__")[0]
						);
						if (replacingFilter) {
							replacingFilterId = replacingFilter.id;
							filterSwappedOnSaveState = item;
							return false;
						} else {
							return true;
						}
					}

					// check for layerUnselectedOnSaveState flag on filter; if it exists, add the filter for the now selected layer
					if (item.layerUnselectedOnSaveState) {
						// if a replacement filter already exists, stop and return to normal
						const replacedFilter = newFilters.find(
							(secondItem: ParaviewFilter) =>
								secondItem.Scalars[1].split("__DATETIME__")[0] ===
								item.Scalars[1].split("__DATETIME__")[0]
						);
						if (replacedFilter && replacedFilter.id !== item.id) {
							item.hideMetadataOnlyFilter = true; // flag to hide the filter when we have a duplicate that isn't immediately replaced; this happens when the layer is active but not selected, and we still need the metadata for when it becomes active
							// sometimes paraview sends a filter that it will delete right after; we need to persist the metadata if it does this
							return true;
						}

						// if the layer isn't selected, we'll get an error adding the filter unless we activate the layer; to avoid errors, just persist the metadata if we're not sure it's safe.
						const layerSelected = displayPane().DISPLAY_STATES?.[viewId]?.[
							datasetId
						].find(
							(layer: any) =>
								layer.layerName == item.layerName && layer.selected === true
						);
						if (!layerSelected) return true;
						item.layerUnselectedOnSaveState = false;
						item.itemAddedPersistMetadata = true;

						pvw().PVW_FILTER_THRESHOLD_ADD({
							layerName,
							viewId,
							field: item.Scalars[1].split("__DATETIME__")[0],
							location: active().SSA_ACTIVE_LAYER_CONFIG.options.field_location,
							dataRange: [
								...(item.disableButDisplayFilter
									? [item.absMin, item.absMax]
									: item.ThresholdRange)
							]
						});

						return true;
					}

					// if an array with this id is on paraview, keep it
					const existsInSecondArray = newFilters.some(
						(secondItem: ParaviewFilter) => secondItem.id === item.id
					);
					if (existsInSecondArray) return true;
				});
				// if a filter was swapped on save state load, update the filter
				if (filterSwappedOnSaveState && replacingFilterId) {
					for (let i = 0; i < cachedFiltersCurrent.length; i++) {
						if (cachedFiltersCurrent[i].id === replacingFilterId) {
							cachedFiltersCurrent[i] = {
								...filterSwappedOnSaveState,
								...cachedFiltersCurrent[i],
								order: filterSwappedOnSaveState.order
							};
							break;
						}
					}
				}

				cachedFiltersCurrent.sort(
					(a, b) => parseInt(a.order ?? "", 10) - parseInt(b.order ?? "", 10)
				);

				cachedFilters[viewId][datasetId].aggregatedFilters = cachedFiltersCurrent;
			}

			return cachedFilters;
		},
		SSA_ALL_ACTIVE_FILTERS(): ParaviewFilterPlus[] {
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedFilters = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			return cachedFilters?.[viewId]?.[datasetId]?.aggregatedFilters ?? [];
		},
		SSA_ALL_ACTIVE_FILTERS_STATE(): FiltersMenuState {
			const viewId = active().SSA_ACTIVE_VIEW;
			let cachedState = this.SSA_ALL_ACTIVE_FILTERS_STATE as any;

			if (this.saveStateFiltersDisplayPane) {
				cachedState = this.saveStateFiltersDisplayPane;
				this.saveStateFiltersDisplayPane = null;
			}

			if (!cachedState) cachedState = {};
			if (!cachedState[viewId]) cachedState[viewId] = {};
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			if (!cachedState[viewId][datasetId])
				cachedState[viewId][datasetId] = {
					visible: true, // always visible
					open: false,
					selected: false
				};

			return cachedState;
		},
		SSA_FILTER_STATE_OPEN(): any {
			const viewId = active().SSA_ACTIVE_VIEW;
			const filtersState = this.SSA_ALL_ACTIVE_FILTERS_STATE;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			return filtersState[viewId][datasetId].open;
		},
		SSA_FILTER_STATE_SELECTED(): any {
			const viewId = active().SSA_ACTIVE_VIEW;
			const filtersState = this.SSA_ALL_ACTIVE_FILTERS_STATE;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			return filtersState[viewId][datasetId].selected;
		}
	},
	actions: {
		async SSA_HIDE_DATASET_FILTERS(
			datasetId: string,
			enabling: boolean,
			skipDisableButDisplayFilter = false, // skipDisableButDisplayFilter and toggleOnlyLayerName are only for mpd-4161 work-around
			toggleOnlyLayerName = ""
		) {
			if (!datasetId) return;
			// adds a flags to prevent UI from removing all unselected filters
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedFilters = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET;
			const filtersToUpdate = [];
			if (
				cachedFilters[viewId][datasetId] &&
				cachedFilters[viewId][datasetId].aggregatedFilters
			) {
				for (const filter of cachedFilters[viewId][datasetId]
					.aggregatedFilters as ParaviewFilterPlus[]) {
					if (!enabling) {
						if (toggleOnlyLayerName !== "" && filter.layerName !== toggleOnlyLayerName)
							continue;
						if (!filter.disableButDisplayFilterSlashEye)
							// if hidden, we keep the current cached values rather than caching the hidden ones
							filter.hiddenRangeValues = [...filter.ThresholdRange];
						let absMin = filter.absMin;
						let absMax = filter.absMax;
						// const absMin = this.SSA_ACTIVE_LAYER_STATE.fields[filter.Scalars[1]].min; // layer may not be active
						// const absMax = this.SSA_ACTIVE_LAYER_STATE.fields[filter.Scalars[1]].max;
						if (!(typeof absMin === "number" && !isNaN(absMin))) absMin = 1; // placeholders for TS; these should always be defined
						if (!(typeof absMax === "number" && !isNaN(absMax))) absMax = -1;

						filter.ThresholdRange = [absMin, absMax];
					} else {
						if (!filter.disableButDisplayFilterSlashEye)
							filter.ThresholdRange = [
								...(filter.hiddenRangeValues ?? filter.ThresholdRange)
							];
					}
					if (!skipDisableButDisplayFilter) filter.disableButDisplayFilter = !enabling;

					if (filter.layerUnselectedOnSaveState || filter.itemAddedPersistMetadata)
						continue; // special case for loading save states with the filter's layer unselected: in this case, we want to wait to make updates until the filter can be added to the selected layer

					filtersToUpdate.push(filter);
				}
				// wait until all filters are updated to send update commands
				for (const filterToUpdate of filtersToUpdate) {
					if (
						displayPane().DISPLAY_STATES[viewId][datasetId].find(
							(layer: any) =>
								layer.layerName == filterToUpdate.layerName &&
								layer.selected === true
						)
					)
						await pvw().PVW_FILTER_THRESHOLD_UPDATE({
							layerName: filterToUpdate.layerName,
							viewId,
							filter: {
								id: filterToUpdate.id,
								ThresholdRange: filterToUpdate.ThresholdRange,
								Scalars: filterToUpdate.Scalars
							} as ParaviewFilter // can send entire payload in other cases, but results in undefined here
						});
				}
			}
			return true;
		},
		SSA_HIDE_FILTER_SHOW_SLASH(filter: ParaviewFilterPlus) {
			// adds a flag to prevent UI from removing the filter and show a slash eye icon
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedFilters = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;

			if (cachedFilters[viewId][datasetId].aggregatedFilters) {
				const filterToHide = cachedFilters[viewId][datasetId].aggregatedFilters.find(
					(cachedFilter: ParaviewFilterPlus) => cachedFilter.id == filter.id
				);
				if (!filterToHide) return;

				const disabling = !filterToHide.disableButDisplayFilterSlashEye;
				console.log("Setting filter for view ", viewId, " hidden = ", disabling);
				if (disabling) {
					// if disabling, send an update with max range values
					filterToHide.hiddenRangeValues = [...filter.ThresholdRange]; // unpack to avoid shared reference
					filterToHide.disableButDisplayFilterSlashEye = true;
					const absMin = active().SSA_ACTIVE_LAYER_STATE.fields[filter.Scalars[1]].min; // could use cached property instead
					const absMax = active().SSA_ACTIVE_LAYER_STATE.fields[filter.Scalars[1]].max;
					filter.ThresholdRange[0] = absMin;
					filter.ThresholdRange[1] = absMax;
					pvw().PVW_FILTER_THRESHOLD_UPDATE({
						layerName: filter.layerName,
						viewId,
						filter
					});
				} else {
					filterToHide.disableButDisplayFilterSlashEye = false;
					// send an update with the cached values we've allowed the user to configure
					filter.ThresholdRange = filterToHide.ThresholdRange = [
						...(filter.hiddenRangeValues ?? filterToHide.ThresholdRange)
					];
					pvw().PVW_FILTER_THRESHOLD_UPDATE({
						layerName: filter.layerName,
						viewId,
						filter
					});
				}

				// send update to paraview to reset
				// pvw().PVW_FILTER_THRESHOLD_UPDATE({ layerName: filter.layerName, viewId, filter });
			}
			return;
		},
		// these values are synced with local component state
		SSA_SET_OPEN_FILTER_STATE(filtersOpen: boolean) {
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedState = this.SSA_ALL_ACTIVE_FILTERS_STATE as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			cachedState[viewId][datasetId].open = filtersOpen;
			return;
		},
		SSA_SET_SELECTED_FILTER_STATE(filtersSelected: boolean): void {
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedState = this.SSA_ALL_ACTIVE_FILTERS_STATE as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			this.SSA_HIDE_DATASET_FILTERS(datasetId, filtersSelected);
			cachedState[viewId][datasetId].selected = filtersSelected;
			this.saveStateFiltersDisplayPane = cachedState;
			return;
		},
		async SSA_TEMP_DISABLE_FILTERS(layerName = ""): Promise<void> {
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			await this.SSA_HIDE_DATASET_FILTERS(datasetId, false, true, layerName);
			setTimeout(async () => {
				// for a large number of filters, we need to use a timeout. For a small number, awaiting both works.
				this.SSA_HIDE_DATASET_FILTERS(datasetId, true, true, layerName);
			}, 100);
			return;
		},
		SSA_FILTERS_ICON_CLICKED(layerName = "") {
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedState = this.SSA_ALL_ACTIVE_FILTERS_STATE as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			cachedState[viewId][datasetId].open = true;
			this.filtersIconClickedLayerName = layerName; // layerName = "" if clicked from hierarchy
			this.filtersIconClickedCount++;
			return;
		},
		SSA_REPLACE_FILTER_LAYER(filter: ParaviewFilterPlus) {
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedFilters = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;

			if (cachedFilters[viewId][datasetId].aggregatedFilters) {
				const filterToUpdate = cachedFilters[viewId][datasetId].aggregatedFilters.find(
					(cachedFilter: ParaviewFilterPlus) => cachedFilter.id == filter.id
				);
				filterToUpdate.hideButKeepOrderPlaceholder = true;
			}

			return;
		},
		SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
			filter: ParaviewFilterPlus,
			minMaxIndex: number,
			newRangeVal: number
		) {
			const viewId = active().SSA_ACTIVE_VIEW;
			const cachedFilters = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET as any;
			const datasetId = (active().SSA_ACTIVE_DATASET as any)?.id;
			console.log("Setting hidden threshold value ", newRangeVal, " for filter ", filter.id);
			if (cachedFilters[viewId][datasetId].aggregatedFilters) {
				const filterToUpdate = cachedFilters[viewId][datasetId].aggregatedFilters.find(
					(cachedFilter: ParaviewFilterPlus) => cachedFilter.id == filter.id
				);
				filterToUpdate.hiddenRangeValues[minMaxIndex] = newRangeVal;
			}

			return;
		},
		// Clone all filter display states for an old view to a new view
		SSA_CLONE_FILTER_STATES_FOR_VIEW(oldViewId: string, newViewId: string) {
			// 1) CLONE FILTER DISPLAY STATES
			const cachedState = this.SSA_ALL_ACTIVE_FILTERS_STATE;
			if (cachedState[oldViewId]) {
				cachedState[newViewId] = {};
				// copy for each dataset id
				const datasetIds = Object.keys(cachedState[oldViewId]);
				datasetIds.forEach((datasetId) => {
					const existingFilters = cachedState[oldViewId][datasetId];
					if (existingFilters) {
						cachedState[newViewId][datasetId] = { ...existingFilters };
					}
				});
				console.log(
					"🚀 Cloning filter menu states from view ",
					oldViewId,
					" to view ",
					newViewId,
					": ",
					cachedState[newViewId]
				);
			}

			// 2) CLONE ACTIVE FILTER METADATA
			// Unfortunately, at this point we don't know what ID paraview will give the new filter, so
			// we can't put it directly in the map - it will just get overwritten when the remote
			// update arrives.  Save the cloned information in a temporary map
			const filtersMetadata = this.SSA_ACTIVE_FILTERS_MAP_BY_DATASET;
			this.cloneInProgressFilters = {} as ActiveFiltersMetadata;
			if (filtersMetadata[oldViewId]) {
				this.cloneInProgressFilters[newViewId] = {};

				const datasetIds = Object.keys(filtersMetadata[oldViewId]);
				datasetIds.forEach((datasetId) => {
					const oldMetadata = filtersMetadata[oldViewId][datasetId];
					if (oldMetadata) {
						// compiler complains about possible null even though we just assigned it
						this.cloneInProgressFilters![newViewId][datasetId] = oldMetadata;
					}
				});

				console.log(
					"🚀 Cloning filter metadata from view ",
					oldViewId,
					" to view ",
					newViewId,
					": ",
					filtersMetadata[newViewId]
				);
			}

			return;
		},
		// Save state filters reassigning
		SSA_LOAD_FILTERS_FROM_SAVE(filtersMap: ActiveFiltersMetadata) {
			this.saveStateFilters = filtersMap;
			this.loadSavedStateFilters = true;
			return;
		},
		//  selected and open states
		SSA_LOAD_FILTERS_DISPLAY_STATE(filtersState: FiltersMenuState) {
			this.saveStateFiltersDisplayPane = filtersState;
			return;
		}
	}
});
